import { useQueries, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import React from 'react';

import { FixedResolvePermissionRequest, roleBindingResourceCompile } from '../../authz/authz.functions';
import { getQueryOptionsPermission } from '../../authz/authz.hooks';
import type {
  ResolvePermissionRequest,
  ResolvePermissionResponse,
} from '../../authz/gen/ai/h2o/authorization/v1/query_api_pb';
import { userName } from '../../authz/gen/ai/h2o/user/v1/user_pb';
import { resourceMLOps } from '../../pages/MLOps/userActions';
import { useWorkspaces } from '../../pages/Orchestrator/WorkspaceProvider';
import { useCloudPlatformDiscovery, useUser } from '../../utils/hooks';

type ResolvedPermission = undefined | boolean; // undefined means "loading"

type ResolvePermissionsRequestWithRequiredAction = Omit<ResolvePermissionRequest, `action`> &
  Pick<Required<ResolvePermissionRequest>, `action`>;

export type PermissionsResolverProps = {
  /**
   * A list of actions to resolve permissions for. If you omit `resource` from the request object, it defaults to
   * an active workspace resource. If you omit `subject` it defaults to the current user. If you supply only a string
   * instead of the resource object, it translates to a current user subject active workspace resource action which is
   * the most common case.
   */
  actions: Array<ResolvePermissionsRequestWithRequiredAction | string>;
  /**
   * A render-prop function with parameters
   * - `allAllowed`: whether all permissions are loaded and allowed.
   * - `loading`: whether any permission is still loading.
   * - `results`: the simplified result of the permission check, `undefined` means loading, otherwise it is true|false
   *      as allowed or not. The `results` corresponds positionally to input `actions`
   */
  children: (params: { allAllowed: boolean; loading: boolean; results: ResolvedPermission[] }) => React.ReactNode;
};

/**
 * A render-props component for resolving permissions that is useful when you cannot use hooks.
 */
export const PermissionsResolver = (props: PermissionsResolverProps) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const currentUser = useUser();
  const currentUserFQDN = userName.compile({ user: currentUser.id });
  const { ACTIVE_WORKSPACE_NAME, activeWorkspaceInitialized } = useWorkspaces();
  const activeWorkspaceResourceName = roleBindingResourceCompile(ACTIVE_WORKSPACE_NAME || ``);
  const queryClient = useQueryClient();
  const queries: UseQueryOptions<ResolvePermissionResponse>[] = props.actions.map((action) => {
    const request: FixedResolvePermissionRequest =
      typeof action === `string`
        ? {
            action: action,
            resource: activeWorkspaceResourceName,
            subject: currentUserFQDN,
          }
        : {
            resource: activeWorkspaceResourceName,
            subject: currentUserFQDN,
            ...action,
          };
    return {
      ...getQueryOptionsPermission(queryClient, authzUrl, request),
      // the query is disabled if it regards active workspace and it is not yet verified
      enabled: request.resource !== activeWorkspaceResourceName || activeWorkspaceInitialized,
    };
  });
  const permissionQueryResults = useQueries({ queries });
  let loading = false;
  let allAllowed = true;
  const results: ResolvedPermission[] = [];
  for (const queryResult of permissionQueryResults) {
    const result: ResolvedPermission = !queryResult.data ? undefined : Boolean(queryResult.data.allowed);
    results.push(result);
    if (result === undefined) {
      loading = true;
      allAllowed = false;
    } else if (result === false) {
      allAllowed = false;
    }
  }
  return <>{props.children({ allAllowed, loading, results })}</>;
};

/**
 * Constructs the RequiredActions for a workspace.
 */
export const currentUserWorkspaceActions = (
  workspaceFQDN: string,
  actions: string[]
): ResolvePermissionsRequestWithRequiredAction[] => {
  const workspaceResourceName = roleBindingResourceCompile(workspaceFQDN);
  return actions.map((action) => ({ action, resource: workspaceResourceName }));
};

/**
 * Constructs the RequiredActions for a global MLOps actions
 */
export const currentUserGlobalMLOpsActions = (actions: string[]): ResolvePermissionsRequestWithRequiredAction[] => {
  return actions.map((action) => ({ action, resource: resourceMLOps }));
};
